import getRandomUUID from '../../generic/utilities/randomuuid/RandomUUID'
import { getWidgetProviderForType } from '../Widgets/base/WidgetProvider'

const toNumberOrUndefined = (value) => {
  if (value === null || value === undefined || value === '') return undefined
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : undefined
}

/**
 * Maps raw widgets (as returned by the backend, with `data` serialized as a
 * JSON string) into the shape the WidgetGrid editor expects: a parsed `data`
 * object and a stable `id` for GridStack keying. Template items have NO id, so a
 * client-side UUID is generated; device-layout widgets DO have an id, which is
 * preserved (so editing/saving a device layout does not churn widget ids). A
 * corrupt `data` falls back to the provider default and is flagged with
 * `invalidConfig`, mirroring MosScreen's device-layout loading.
 *
 * Lives in its own module because it pulls the widget registry and the ESM
 * `uuid` dependency; keep lightweight save-only consumers off this import.
 */
export const parseTemplateWidgets = (rawWidgets) => {
  if (!Array.isArray(rawWidgets)) return []

  return rawWidgets.map((raw) => {
    const element = { ...raw }
    let parsed = {}
    let invalid = false

    if (element.data) {
      if (typeof element.data === 'string') {
        try {
          parsed = JSON.parse(element.data)
        } catch (err) {
          invalid = true
        }
      } else {
        parsed = element.data
      }
    }

    const provider = getWidgetProviderForType(element.type)
    if (invalid) {
      element.data = provider.getDefaultConfiguration ? provider.getDefaultConfiguration() : {}
      element.invalidConfig = true
    } else {
      element.data = parsed
    }

    const info = provider.getWidgetInfo()

    element.id = element.id !== undefined && element.id !== null ? String(element.id) : getRandomUUID()
    element.x = toNumberOrUndefined(element.x)
    element.y = toNumberOrUndefined(element.y)
    element.w = toNumberOrUndefined(element.w) ?? info.defaultW
    element.h = toNumberOrUndefined(element.h) ?? info.defaultH
    element.minW = toNumberOrUndefined(element.minW)
    element.minH = toNumberOrUndefined(element.minH)
    element.maxW = toNumberOrUndefined(element.maxW)
    element.maxH = toNumberOrUndefined(element.maxH)

    return element
  })
}

/**
 * Returns a copy of the widgets with each widget's `data` reset to its provider
 * default configuration. Used when creating a template "without copying the
 * widget settings": the template keeps the layout (types/positions/sizes) but
 * drops the specific per-widget configuration, leaving valid default config so
 * the widgets still render when the template is applied.
 */
export const stripWidgetSettings = (widgets) => {
  if (!Array.isArray(widgets)) return []
  return widgets.map((widget) => {
    const provider = getWidgetProviderForType(widget.type)
    return { ...widget, data: provider.getDefaultConfiguration ? provider.getDefaultConfiguration() : {} }
  })
}
